import React, { useMemo } from 'react'
import type { Asset, SmpteController } from '@/organisms/AngelPlayer/BitmovinPlayer/types'
import { HlsBufferingOverlay } from './HlsBufferingOverlay'
import { HlsCenterPlayOverlay } from './HlsCenterPlayOverlay'
import { HlsControlBar } from './HlsControlBar'
import { HlsPlayerUIProvider } from './HlsPlayerUIContext'
import { HlsTitleBar } from './HlsTitleBar'
import { useControlBarVisibility } from './useControlBarVisibility'
import { useVideoState } from './useVideoState'

interface HlsPlayerUIProps {
  videoRef: React.RefObject<HTMLVideoElement | null>
  controller: SmpteController | null
  containerRef: React.RefObject<HTMLElement | null>
  asset: Asset
}

/**
 * Top-level UI overlay for the HLS.js player.
 * Sets up the HlsPlayerUIContext (video state + controller) and renders:
 * - Center play/pause overlay (click-to-play on video area)
 * - Bottom control bar with all playback controls
 */
export const HlsPlayerUI: React.FC<HlsPlayerUIProps> = ({ videoRef, controller, containerRef, asset }) => {
  const videoState = useVideoState(videoRef, containerRef)
  const { visible, onControlBarInteraction } = useControlBarVisibility(containerRef, videoState.playing)

  const contextValue = useMemo(
    () => ({
      currentTime: videoState.currentTime,
      duration: videoState.duration,
      buffered: videoState.buffered,
      playing: videoState.playing,
      volume: videoState.volume,
      muted: videoState.muted,
      fullscreen: videoState.fullscreen,
      waiting: videoState.waiting,
      controller,
      title: asset.sourceConfig.subtitle,
      videoRef,
    }),
    [videoState, controller, asset.sourceConfig.subtitle, videoRef],
  )

  return (
    <HlsPlayerUIProvider value={contextValue}>
      <HlsTitleBar visible={visible} />
      <HlsBufferingOverlay />
      <HlsCenterPlayOverlay visible={visible} />
      <HlsControlBar visible={visible} onInteraction={onControlBarInteraction} />
    </HlsPlayerUIProvider>
  )
}
